import { DateTime } from "luxon";
import { z } from "zod/v4";

import { DatasetId } from "./base";

export enum GraphType {
  Histogram = "histogram",
  StackedBar = "stackedBar",
  StackedBarHorizontal = "stackedBarHorizontal",
  Bar = "bar",
  StackedArea = "stackedArea",
  SyncedBar = "syncedBar",
  ClusteredBar = "clusteredBar",
  BiaxialBar = "biaxialBar",
  Line = "line",
  Sankey = "sankey",
  Table = "table",
  OrderValue = "orderValue",
  ProfitAndRevenue = "profitAndRevenue",
  AudienceByMetric = "audienceByMetric",
  UnitQuantityByMetric = "unitQuantityByMetric",
}

export const DateTypeList = [
  "numberNoDecimal",
  "number",
  "currency",
  "currencyNoDecimal",
  "percent",
  "percentTwoDecimal",
  "percentNoDecimal",
  "numberNoRounding",
  "bfcmPercent",
  "none",
] as const;

export type DataType = (typeof DateTypeList)[number];

export type GraphGlobalMetric = {
  label: string;
  value: number;
  dataType?: DataType;
};

export type GraphConfig = {
  title: string[];
  titleTooltip?: any;
  /** Shown under the title, for charts whose scale needs a sentence. */
  subtitle?: string;
  metrics: string[];
  canBeKeyMetric?: boolean;
  dataTypes: DataType[];
  datasetId: DatasetId;
  isImpactGraph?: boolean;
  graphType: GraphType;
  stackedBarLegendTitles?: string[];
  showSumStackedLabels?: boolean;
  filterMetric?: string;
  histogramBucket?: string;
  showGlobalMetrics?: boolean;
  hasMetricFilterIndex?: boolean; // has two metrics to switch between
  showLegend?: boolean; // hooked up to line graph currently
  hasProbability?: boolean;
  hasTimeseries?: boolean;
  hasValueCI?: boolean; // confidence interval
  hasAudience?: boolean; // in audience table
  maxGroupAmt?: number;
  hasKDE?: boolean;
  hasCDF?: boolean;
  impactMetric?: string;
  hasImpactCalculation?: boolean;
  impactCalculationColoring?: "inverted" | "neutral" | "normal";
  denominatorMetric?: string; // population metric used as denominator (default: "n_visitors")
  isCustomMetric?: boolean;
  showEveryXAxisTick?: boolean;
  intervalOfTheData?: number;
};

export type LineData = {
  animationDuration?: number;
  color?: string;
  dataType?: DataType;
  id: string;
  isDotted?: true;
  name: string;
  showEndDot?: boolean;
};

export type GraphLegend = {
  id: string;
  name: string;
  dataType?: DataType;
};

export type SnapshotDataset = {
  pct_change: number;
  prev_value: number;
  value: number;
};

export type SnapshotAudienceDataset = {
  pct_change: number | null;
  value: number;
  pct_impact_to_total_change: number;
  impact_from_visitor_mix_shift?: number;
};

export type SnapshotAudienceQuery = Record<
  string,
  Record<string, SnapshotAudienceDataset>
>;

export type SnapshotAudienceMetadata = {
  audience: string;
  comparison_end_ts: string;
  comparison_start_ts: string;
  end_ts: string;
  start_ts: string;
};

export enum CogsStatus {
  valid = "valid",
  insufficient = "insufficient",
}

export type RangePickerPreset = {
  label: string;
  value: [DateTime, DateTime];
};

// --- Generic Graph Generation ---

export const CustomGraphTypeEnum = [
  "bar",
  "line",
  "stacked_bar",
  "pie",
  "doughnut",
  "radar",
  "polar_area",
  "scatter",
] as const;

export type GenericGraphType = (typeof CustomGraphTypeEnum)[number];

export const GenericGraphDatasetSchema = z.object({
  label: z.string().describe("Series name (legend entry)"),
  data: z.array(z.number()).describe("Values, length should match labels"),
});

export type GenericGraphDataset = z.infer<typeof GenericGraphDatasetSchema>;

export const GenericGraphScatterDatasetSchema = z.object({
  label: z.string().describe("Series name (legend entry)"),
  data: z
    .array(z.object({ x: z.number(), y: z.number() }))
    .describe("Scatter plot data points"),
});

export type GenericGraphScatterDataset = z.infer<
  typeof GenericGraphScatterDatasetSchema
>;

export const CustomGraphOptionsSchema = z.object({
  xAxisLabel: z.string().optional().describe("X-axis title"),
  yAxisLabel: z.string().optional().describe("Y-axis title"),
  dataType: z
    .enum(["number", "currency", "percent"])
    .optional()
    .describe("Value formatting. Default: number"),
  currencyCode: z
    .string()
    .optional()
    .describe("Currency code (e.g. USD). Required if dataType is currency"),
  showLegend: z
    .boolean()
    .optional()
    .describe("Show legend. Default: true if multiple datasets"),
  showDataLabels: z
    .boolean()
    .optional()
    .describe("Show values on bars/points. Default: false"),
});

export type GenericGraphOptions = z.infer<typeof CustomGraphOptionsSchema>;

export const GenerateCustomGraphRequestBodySchema = z.object({
  title: z.string().describe("Chart title"),
  graphType: z
    .enum(CustomGraphTypeEnum)
    .describe(
      "Chart type: bar, line, stacked_bar, pie, doughnut, radar, polar_area, or scatter"
    ),
  labels: z
    .array(z.string())
    .describe("X-axis labels or segment labels for pie/doughnut"),
  datasets: z
    .union([
      z.array(GenericGraphDatasetSchema),
      z.array(GenericGraphScatterDatasetSchema),
    ])
    .describe("Array of data series"),
  options: CustomGraphOptionsSchema.optional(),
});

export type GenerateCustomGraphRequestBody = z.infer<
  typeof GenerateCustomGraphRequestBodySchema
>;

export const CustomGraphOutputSchema = z.object({
  url: z
    .string()
    .describe(
      "URL of the rendered graph image. Expires after 30 days — download and store externally if you need to retain it longer."
    ),
});

export type CustomGraphOutput = z.infer<typeof CustomGraphOutputSchema>;
